/* eslint-disable react/prop-types */
import React, { Component } from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "components/CustomButtons/Button.jsx";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import SnackbarContent from "components/Snackbar/SnackbarContent.jsx";
import { requestUserPlan } from "../../services/planDataProvider";

const styles = {
  dialogPaper: {
    minHeight: "80vh",
    maxHeight: "90vh"
  },
  sectionTitle: {
    fontWeight: 600,
    marginTop: "20px",
    marginBottom: "12px",
    color: "#333",
    fontSize: "1.1rem"
  },
  formSection: {
    marginBottom: "24px"
  }
};

const TRAINING_MOTIVES = [
  { value: "race", label: "Prepare for a Race" },
  { value: "speed", label: "Improve Speed" },
  { value: "fitness", label: "General Fitness Improvement" }
];

const RACE_DISTANCES = [
  { value: "5k", label: "5K" },
  { value: "10k", label: "10K" },
  { value: "half_marathon", label: "Half Marathon" },
  { value: "marathon", label: "Marathon" },
  { value: "ultra", label: "Ultra Marathon" },
  { value: "other", label: "Other" }
];

const TRAINING_DAYS = [
  { value: 1, label: "1 day" },
  { value: 2, label: "2 days" },
  { value: 3, label: "3 days" },
  { value: 4, label: "4 days" },
  { value: 5, label: "5 days" },
  { value: 6, label: "6 days" },
  { value: 7, label: "7 days" }
];

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday"
];

const RUN_TYPES = [
  { key: "longRun", label: "Long Run Day" },
  { key: "easyRun", label: "Easy Run Days" },
  { key: "intervalRun", label: "Interval/Tempo Run Day" },
  { key: "restDay", label: "Rest Days" }
];

class RequestTrainingProgram extends Component {
  constructor(props) {
    super(props);
    this.state = {
      formData: {
        trainingMotive: "",
        raceDistance: "",
        raceDate: "",
        weeklyMileage: "",
        lastRaceDistance: "",
        lastRaceTime: "",
        age: "",
        weight: "",
        trainingDaysPerWeek: "",
        longRunDay: [],
        easyRunDay: [],
        intervalRunDay: [],
        restDay: []
      },
      errors: {},
      isSubmitting: false,
      successMessage: "",
      errorMessage: ""
    };
  }

  handleChange = field => event => {
    const value = event.target.value;
    this.setState({
      formData: {
        ...this.state.formData,
        [field]: value
      },
      errors: {
        ...this.state.errors,
        [field]: ""
      }
    });
  };

  handleDaySelection = (runType, day) => event => {
    const isChecked = event.target.checked;
    const currentDays = this.state.formData[runType] || [];

    let updatedDays;
    if (isChecked) {
      updatedDays = [...currentDays, day];
    } else {
      updatedDays = currentDays.filter(d => d !== day);
    }

    this.setState({
      formData: {
        ...this.state.formData,
        [runType]: updatedDays
      }
    });
  };

  getAllSelectedDays = () => {
    const { formData } = this.state;
    const allDays = [];
    RUN_TYPES.forEach(runType => {
      const days = formData[runType.key] || [];
      allDays.push(...days);
    });
    return allDays;
  };

  isDayDisabled = (currentRunType, day) => {
    const { formData } = this.state;
    const allSelectedDays = this.getAllSelectedDays();
    const currentRunTypeDays = formData[currentRunType] || [];
    const trainingDaysPerWeek = formData.trainingDaysPerWeek || 7;

    // Disable all days if training days per week is not selected
    if (!formData.trainingDaysPerWeek) {
      return true;
    }

    // Check if day is already selected in another run type
    const isDayInOtherRunType = RUN_TYPES.some(runType => {
      if (runType.key === currentRunType) return false;
      const days = formData[runType.key] || [];
      return days.includes(day);
    });

    // Check if we've reached the training days limit
    const totalSelectedDays = allSelectedDays.length;
    const isDayAlreadySelectedInCurrentType = currentRunTypeDays.includes(day);
    const isLimitReached =
      totalSelectedDays >= trainingDaysPerWeek &&
      !isDayAlreadySelectedInCurrentType;

    return isDayInOtherRunType || isLimitReached;
  };

  validateForm = () => {
    const { formData } = this.state;
    const errors = {};

    if (!formData.trainingMotive) {
      errors.trainingMotive = "Please select a training motive";
    }

    if (formData.trainingMotive === "race") {
      if (!formData.raceDistance) {
        errors.raceDistance = "Please select a race distance";
      }
      if (!formData.raceDate) {
        errors.raceDate = "Please select a race date";
      }
    }

    if (!formData.weeklyMileage) {
      errors.weeklyMileage = "Please enter your weekly mileage";
    }

    if (!formData.age) {
      errors.age = "Please enter your age";
    }

    if (!formData.weight) {
      errors.weight = "Please enter your weight";
    }

    if (!formData.trainingDaysPerWeek) {
      errors.trainingDaysPerWeek = "Please select training days per week";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmit = async () => {
    if (!this.validateForm()) {
      this.setState({
        errorMessage: "Please fill in all required fields"
      });
      return;
    }

    this.setState({ isSubmitting: true, errorMessage: "", successMessage: "" });

    try {
      const response = await requestUserPlan(
        this.props.axios,
        this.state.formData
      );
      this.setState({
        isSubmitting: false,
        successMessage:
          "Training program request submitted successfully! Our system will generate your personalized plan shortly."
      });

      // Close after 3 seconds
      setTimeout(() => {
        if (this.props.onSuccess) {
          this.props.onSuccess(response);
        }
        this.props.onClose();
      }, 3000);
    } catch (error) {
      this.setState({
        isSubmitting: false,
        errorMessage:
          "Failed to submit training program request. Please try again."
      });
    }
  };

  renderFormFields = (
    formData,
    errors,
    errorMessage,
    successMessage,
    readOnly
  ) => {
    return (
      <Grid
        container
        spacing={3}
        style={readOnly ? { pointerEvents: "none", opacity: 0.9 } : undefined}
      >
        {/* Training Motive Section */}
        <Grid item xs={12}>
          <div className={this.props.classes.formSection}>
            <Typography className={this.props.classes.sectionTitle}>
              Training Goal
            </Typography>
            <TextField
              select
              fullWidth
              variant="outlined"
              label="What is your training motive?"
              value={formData.trainingMotive}
              onChange={this.handleChange("trainingMotive")}
              error={Boolean(errors.trainingMotive)}
              helperText={errors.trainingMotive}
              required
            >
              {TRAINING_MOTIVES.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </Grid>

        {/* Race Details (conditional) */}
        {formData.trainingMotive === "race" && (
          <React.Fragment>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Target Race Distance"
                value={formData.raceDistance}
                onChange={this.handleChange("raceDistance")}
                error={Boolean(errors.raceDistance)}
                helperText={errors.raceDistance}
                required
              >
                {RACE_DISTANCES.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Target Race Date"
                type="date"
                value={formData.raceDate}
                onChange={this.handleChange("raceDate")}
                error={Boolean(errors.raceDate)}
                helperText={errors.raceDate}
                InputLabelProps={{
                  shrink: true
                }}
                required
              />
            </Grid>
          </React.Fragment>
        )}

        {/* Current Fitness Assessment */}
        <Grid item xs={12}>
          <Typography className={this.props.classes.sectionTitle}>
            Current Fitness Level
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            variant="outlined"
            label="Weekly Mileage (last 4 weeks average)"
            type="number"
            value={formData.weeklyMileage}
            onChange={this.handleChange("weeklyMileage")}
            error={Boolean(errors.weeklyMileage)}
            helperText={errors.weeklyMileage || "kms per week"}
            required
            InputProps={{
              inputProps: { min: 0 }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            variant="outlined"
            label="Last Race Distance"
            value={formData.lastRaceDistance}
            onChange={this.handleChange("lastRaceDistance")}
            helperText="e.g., 5K, 10K, Half Marathon"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            variant="outlined"
            label="Last Race Time"
            value={formData.lastRaceTime}
            onChange={this.handleChange("lastRaceTime")}
            helperText="e.g., 25:30, 1:45:00"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            variant="outlined"
            label="Age"
            type="number"
            value={formData.age}
            onChange={this.handleChange("age")}
            error={Boolean(errors.age)}
            helperText={errors.age}
            required
            InputProps={{
              inputProps: { min: 10, max: 100 }
            }}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            variant="outlined"
            label="Weight"
            type="number"
            value={formData.weight}
            onChange={this.handleChange("weight")}
            error={Boolean(errors.weight)}
            helperText={errors.weight || "kg"}
            required
            InputProps={{
              inputProps: { min: 50, max: 500 }
            }}
          />
        </Grid>

        {/* Training Schedule */}
        <Grid item xs={12}>
          <Typography className={this.props.classes.sectionTitle}>
            Training Schedule Preferences
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            variant="outlined"
            label="Training Days Per Week"
            value={formData.trainingDaysPerWeek}
            onChange={this.handleChange("trainingDaysPerWeek")}
            error={Boolean(errors.trainingDaysPerWeek)}
            helperText={
              errors.trainingDaysPerWeek ||
              "Tip: 4 days per week is a good starting point for most runners"
            }
            required
          >
            {TRAINING_DAYS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Typography
            variant="body2"
            style={{ marginBottom: "12px", color: "#666" }}
          >
            {formData.trainingDaysPerWeek ? (
              <span>
                Select your preferred days for different types of workouts
                {` (${this.getAllSelectedDays().length}/${
                  formData.trainingDaysPerWeek
                } days selected)`}
                :
              </span>
            ) : (
              <span>
                Please select training days per week first to choose your
                preferred workout days
              </span>
            )}
          </Typography>
        </Grid>

        {RUN_TYPES.map(runType => (
          <Grid item xs={12} sm={6} key={runType.key}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{runType.label}</FormLabel>
              <FormGroup>
                {DAYS_OF_WEEK.map(day => {
                  const isDisabled = this.isDayDisabled(runType.key, day);
                  const isChecked =
                    formData[runType.key] &&
                    formData[runType.key].includes(day);
                  return (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={this.handleDaySelection(runType.key, day)}
                          color="primary"
                          disabled={readOnly || isDisabled}
                        />
                      }
                      label={day}
                      disabled={readOnly || isDisabled}
                    />
                  );
                })}
              </FormGroup>
            </FormControl>
          </Grid>
        ))}

        {/* Messages */}
        {errorMessage && (
          <Grid item xs={12}>
            <SnackbarContent message={errorMessage} color="danger" />
          </Grid>
        )}

        {successMessage && (
          <Grid item xs={12}>
            <SnackbarContent message={successMessage} color="success" />
          </Grid>
        )}
      </Grid>
    );
  };

  render() {
    const {
      classes,
      open,
      onClose,
      readOnly,
      embedded,
      readOnlyData
    } = this.props;
    const {
      formData: stateFormData,
      errors,
      isSubmitting,
      successMessage,
      errorMessage
    } = this.state;

    const formData =
      readOnly && readOnlyData
        ? {
            ...stateFormData,
            ...readOnlyData
          }
        : stateFormData;

    if (embedded) {
      return (
        <div>
          <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 8 }}>
            Request Training Program
          </Typography>
          <Typography
            variant="body2"
            style={{ color: "#666", marginBottom: 16 }}
          >
            Submitted request details
          </Typography>
          {this.renderFormFields(
            formData,
            errors,
            errorMessage,
            successMessage,
            Boolean(readOnly)
          )}
        </div>
      );
    }

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Request Training Program
          </Typography>
          <Typography variant="body2" style={{ color: "#666", marginTop: 8 }}>
            Tell us about your goals and fitness level to get a personalized
            training plan
          </Typography>
        </DialogTitle>

        <DialogContent>
          {this.renderFormFields(
            formData,
            errors,
            errorMessage,
            successMessage,
            Boolean(readOnly)
          )}
        </DialogContent>

        {!readOnly && (
          <DialogActions style={{ padding: "16px 24px" }}>
            <Button onClick={onClose} color="default" disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={this.handleSubmit}
              color="info"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }
}

RequestTrainingProgram.propTypes = {
  axios: PropTypes.any.isRequired,
  embedded: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  readOnlyData: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

export default withStyles(styles)(RequestTrainingProgram);
